import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ShiftContext } from '@/contexts/ShiftContext';
import { selectOnboardingPatientId } from '@/lib/active-patient';
import type { Tables } from '@/integrations/supabase/types';

type Patient = Tables<'patients'>;

interface FocusedPatientContextType {
  focusedPatient: Patient | null;
  setFocusedPatientId: (patientId: string | null) => void;
  isLoading: boolean;
}

export const FocusedPatientContext = createContext<FocusedPatientContextType | undefined>(undefined);

const STORAGE_KEY = 'careos_focused_patient_id';

export function FocusedPatientProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const shiftContext = useContext(ShiftContext);
  const activeShift = shiftContext?.activeShift ?? null;
  const shiftLoading = shiftContext?.isLoading ?? true;
  const [focusedPatientId, setFocusedPatientIdState] = useState<string | null>(null);
  const hasAutoFocused = useRef(false);

  // Initialize from localStorage on mount or when user changes
  useEffect(() => {
    if (!user) {
      setFocusedPatientIdState(null);
      hasAutoFocused.current = false;
      return;
    }
    const storedId = localStorage.getItem(`${STORAGE_KEY}_${user.id}`);
    setFocusedPatientIdState(storedId);
  }, [user]);

  // Auto-focus on active shift patient when shift data loads
  useEffect(() => {
    if (!user || shiftLoading || hasAutoFocused.current) return;
    hasAutoFocused.current = true;

    if (activeShift) {
      localStorage.setItem(`${STORAGE_KEY}_${user.id}`, activeShift.patient_id);
      setFocusedPatientIdState(activeShift.patient_id);
    }
  }, [user, shiftLoading, activeShift]);

  // Use useQuery to fetch and cache focused patient data
  // This queryKey matches the one in PatientDetail, enabling cache sharing
  const { data: focusedPatient = null, isLoading: isQueryLoading } = useQuery({
    queryKey: ['patient', focusedPatientId],
    queryFn: async () => {
      if (!focusedPatientId || !user) return null;

      // Transient failures (network, token refresh) must NOT clear the focus:
      // throwing lets React Query retry and keeps the previous value.
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('id', focusedPatientId)
        .maybeSingle();

      if (error) throw error;

      const { data: link, error: linkError } = await supabase
        .from('patient_user_links')
        .select('id')
        .eq('patient_id', focusedPatientId)
        .eq('user_id', user.id)
        .eq('active', true)
        .maybeSingle();

      if (linkError) throw linkError;

      // Confirmed: patient gone or access revoked → drop the stored focus so the
      // auto-selection below can pick another patient.
      if (!data || !link) {
        localStorage.removeItem(`${STORAGE_KEY}_${user.id}`);
        setFocusedPatientIdState(null);
        return null;
      }

      return data as Patient;
    },
    enabled: !!focusedPatientId && !!user,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Auto-select a patient when there is no focus at all (first access, or access
  // confirmedly removed). Prefers active links to active patients, owner first.
  const { data: autoCandidateId } = useQuery({
    queryKey: ['focused-patient-auto-select', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('patient_user_links')
        .select('patient_id, role, created_at, active, patients!inner(status)')
        .eq('user_id', user.id)
        .eq('active', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return selectOnboardingPatientId(data as any);
    },
    enabled: !!user && !focusedPatientId,
    staleTime: 60 * 1000,
    retry: 2,
  });

  useEffect(() => {
    if (!user || focusedPatientId || !autoCandidateId) return;
    localStorage.setItem(`${STORAGE_KEY}_${user.id}`, autoCandidateId);
    setFocusedPatientIdState(autoCandidateId);
  }, [user, focusedPatientId, autoCandidateId]);

  const setFocusedPatientId = useCallback((patientId: string | null) => {
    if (!user) return;

    if (patientId === null) {
      localStorage.removeItem(`${STORAGE_KEY}_${user.id}`);
      setFocusedPatientIdState(null);
      return;
    }

    localStorage.setItem(`${STORAGE_KEY}_${user.id}`, patientId);
    setFocusedPatientIdState(patientId);
  }, [user]);

  const isLoading = !!focusedPatientId && isQueryLoading;

  return (
    <FocusedPatientContext.Provider value={{ focusedPatient, setFocusedPatientId, isLoading }}>
      {children}
    </FocusedPatientContext.Provider>
  );
}
